import { Form, FormControlProps, InputGroup } from 'react-bootstrap';
import { Currency } from '@website/types/graphqlOperations';
import {
  useController,
  UseControllerReturn,
  UseFormReturn,
} from 'react-hook-form';
import currencies from '../../../dataObjects/currencies';

/**
 * Handle Price Field changes
 *
 * We allow an input in dollars (e.g. 100.00) but want form values to update in zero-decimal cents (e.g. 10000).
 */
export function handleChange(
  value: string,
  onChange: UseControllerReturn['field']['onChange'],
) {
  if (value !== '') {
    const zeroDecimalValue = (parseFloat(value) * 100).toFixed();
    const zeroDecimalValueInt = parseInt(zeroDecimalValue);
    onChange(zeroDecimalValueInt);
  }
}

/**
 * Price Field
 *
 * Creates a number field, prepended with an {@link InputGroup} with the currency symbol. Lets the user set the value in
 * dollars but send the value to your submit handler in zero-decimal cents.
 */
export default function PriceField({
  currency,
  control,
  name,
  required = false,
}: PriceFieldProps) {
  const {
    field: { onChange, value },
  } = useController({ name, control, rules: { required } });

  const currencyDetails = currencies[currency];

  const valueDecimals = parseFloat(value ?? 0) / 100;

  return (
    <InputGroup>
      <InputGroup.Text>{currencyDetails.symbol}</InputGroup.Text>
      <Form.Control
        type="number"
        style={{ minWidth: 100 }}
        step="0.01"
        onChange={(e) => handleChange(e.target.value, onChange)}
        value={valueDecimals}
        aria-label="Price"
      />
    </InputGroup>
  );
}

export type PriceFieldProps = FormControlProps & {
  control: UseFormReturn<any>['control'];
  currency: Currency;
  name: string;
  required?: boolean;
};
